import "mapbox-gl/dist/mapbox-gl.css";
import React, { useEffect, useState, useRef } from "react";
import MapGL, { Marker } from "react-map-gl";
import { CircularProgress } from "@material-ui/core";

import Pin from "../map/pin";
import { defaultConfiguration, defaultBivariateConfiguration } from "./UniversalDefaults";
import { renderDownloadButton } from "./DebugDownloadButton";
import { requestAndApplyFilter } from "./ApplyFilter";
import { LegendHelper } from "./LegendHelper";
import { fillLayer, printNoContrattiAtPoint } from "./LayerHelper";
import { createMarkers, smallPopupBoxCreation } from "./MarkerHelper";
import { MapStyleSelectorTool } from "./MapStyleSelectorHelper";
import { HazardSelectorTool } from "./HazardSelectorHelper";
import { KpiSelectorTool } from "./KPISelectorHelper";
import { getMapComuneData } from "../util/MapUtils";
import { filterModelToQueryValue, getCompanyCodeName } from "../util/tableUtils";
import httpClient from "../../lib/httpClient";
import { mapHazardCodeToHazardProperty, getSubintervals, getSimplifiedFromAnia } from "../map/BivariateLogic";

import { apiError } from "../../lib/apiError";
import { ScaleBar } from "../map/ScaleBar";
import {
  defaultRomeLatitude,
  defaultRomeLongitude,
  buildingMapStyleName,
  agricultureMapStyleName,
} from "../map/Constants";
import { useClientConfig } from "../../context/clientConfigContext";

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const site = "https://d24ye0ax7gglwd.cloudfront.net/";
//const site = "https://cys-realerisk-mapdata-public.s3.eu-west-1.amazonaws.com/";

const suffixRegionePoints = "regione_points_2023.geojson";
const suffixProvPoints = "prov_points_2023.geojson";
const suffixComunePoints = "comune_points_2023.geojson";
const suffixRegione = "regione_2023.geojson";
const suffixProv = "prov_2023.geojson";
const suffixComune = "comune_2023.geojson";

const filePrefix = {
  "agriculture-policies": ["pageview-agriculture-policies", "dbagriculture_contratti_"],
  "buildings-policies": ["pageview-buildings-policies", "dbbuildings_policies_"],
  "agriculture-claims": ["pageview-agriculture-claims", "dbagriculture_denunce_"],
  "buildings-claims": ["pageview-buildings-claims", "dbbuildings_claims_"],
};

export const UniversalMap = ({
  hazardSelect = "anyhazard",
  setHazardSelect,
  KPISelect = "none",
  setKPISelect,
  ...props
}) => {
  const mapRef = useRef(null);
  const config = useClientConfig();

  const [viewState, setViewState] = useState({
    latitude: defaultRomeLatitude,
    longitude: defaultRomeLongitude,
    zoom: 6,
  });

  const isBuildingsFlag = () => {
    return props.page === "buildings-policies" || props.page === "buildings-claims";
  };
  const defaultMapBoxStyle = () => {
    return isBuildingsFlag() ? buildingMapStyleName : agricultureMapStyleName;
  };

  const [selectedPolicy, setSelectedPolicy] = useState(null);

  const [currentZoom, setCurrentZoom] = useState(6);
  const [mapSelect, setMapSelect] = useState(defaultMapBoxStyle());

  // This flag is needed in order to avoid rendering the bivariate layer before property is added to geojson
  const [bivariateAdded, setBivariateAdded] = useState(false);
  const [isLoading, setIsLoading] = useState(0);
  const [mapStyle, setMapStyle] = useState("mapbox://styles/" + defaultMapBoxStyle());
  //const [mapStyle, setMapStyle] = useState(lightMapStye);

  const [hazardSelectDone, setHazardSelectDone] = useState("-");
  const [KPISelectDone, setKPISelectDone] = useState("-");

  // Note: all they should be states. Normal geojsons are loaded from URL,
  // whereas filtered geojsons are computed from the normal ones when Filters are applied.
  // Filtered ones are then used to render the map.

  // Note: To refactor all this to a hierarchical structure of components,
  // did not work well, because all they are interdependent.
  // It look like they lose some branches of the tree.

  const [geojsonDataComunePoints, setGeojsonDataComunePoints] = useState(null);
  const [geojsonDataComunePointsFiltered, setGeojsonDataComunePointsFiltered] = useState(null);
  const [geojsonDataComunePolygons, setGeojsonDataComunePolygons] = useState(null);
  const [geojsonDataComunePolygonsFiltered, setGeojsonDataComunePolygonsFiltered] = useState(null);

  const [geojsonDataProvPoints, setGeojsonDataProvPoints] = useState(null);
  const [geojsonDataProvPointsFiltered, setGeojsonDataProvPointsFiltered] = useState(null);
  const [geojsonDataProvPolygons, setGeojsonDataProvPolygons] = useState(null);
  const [geojsonDataProvPolygonsFiltered, setGeojsonDataProvPolygonsFiltered] = useState(null);

  const [geojsonDataRegionePoints, setGeojsonDataRegionePoints] = useState(null);
  const [geojsonDataRegionePointsFiltered, setGeojsonDataRegionePointsFiltered] = useState(null);
  const [geojsonDataRegionePolygons, setGeojsonDataRegionePolygons] = useState(null);
  const [geojsonDataRegionePolygonsFiltered, setGeojsonDataRegionePolygonsFiltered] = useState(null);

  const isClaimsFlag = props.page === "agriculture-claims" || props.page === "buildings-claims";

  const isRiskYearsFlag = props.variantProp && props.variantProp === "ClimateAnalytics";

  const allJsons = {
    comune: defaultConfiguration(props, 7, 11.5, "comune", isClaimsFlag, isRiskYearsFlag),
    prov: defaultConfiguration(props, 5.5, 7, "provincia", isClaimsFlag, isRiskYearsFlag),
    regione: defaultConfiguration(props, 2, 5.5, "regione", isClaimsFlag, isRiskYearsFlag),
    bivariate: defaultBivariateConfiguration(),
  };

  const isMountedRef = useRef(true); // Using a ref to store the mounted status
  // This is needed to avoid run-time warning about leaks.
  // See why is it need here:
  // src\components\map\ExplanationOfIsMountedRef.js for more details

  useEffect(() => {
    //console.log("useEffect,fetchComuneGeoJson");
    fetchComuneGeoJson(allJsons.comune.propertyName);
    fetchProvGeoJson(allJsons.prov.propertyName);
    fetchRegioneGeoJson(allJsons.regione.propertyName);
    return () => {
      isMountedRef.current = false; // Update the ref when unmounting
    };
    // It runs only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    //console.log("useEffect,renderApplyFilterButtonParams");
    const renderApplyFilterButtonParams = {
      geojsonDataComunePoints,
      setGeojsonDataComunePointsFiltered,
      geojsonDataComunePolygons,
      setGeojsonDataComunePolygonsFiltered,
      geojsonDataProvPoints,
      setGeojsonDataProvPointsFiltered,
      geojsonDataProvPolygons,
      setGeojsonDataProvPolygonsFiltered,
      geojsonDataRegionePoints,
      setGeojsonDataRegionePointsFiltered,
      geojsonDataRegionePolygons,
      setGeojsonDataRegionePolygonsFiltered,
      allJsons,
    };

    if (
      geojsonDataComunePoints &&
      geojsonDataComunePolygons &&
      geojsonDataProvPoints &&
      geojsonDataProvPolygons &&
      geojsonDataRegionePoints &&
      geojsonDataRegionePolygons &&
      !isBivariate()
    )
      requestAndApplyFilter(renderApplyFilterButtonParams, props.filteredMapDataProp);
    else {
      console.log(
        "Filtering cannot be applied: waiting for geojson data to be loaded..., props.filteredMapDataProp = ",
        props.filteredMapDataProp,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filteredMapDataProp]);

  useEffect(() => {
    //console.log("useEffect,geojsonDataComunePolygonsFiltered");
    if (isBivariate() && geojsonDataComunePolygonsFiltered) {
      getJsonWithKPIforPeril("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filteredMapDataProp]);

  useEffect(() => {
    const updateMapByComuneData = async () => {
      const buttonProps = {
        filterModel: {},
        routeName: props.routeNameProp,
        setFilteredMapData: props.setFilteredMapDataProp,
        variant: props.variantProp,
        wantedReferenceDate: props.wantedReferenceDate,
      };
      setIsLoading((old) => old + 1);
      await getMapComuneData(buttonProps);
      setIsLoading((old) => old - 1);
    };
    if (geojsonDataComunePolygons) {
      updateMapByComuneData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojsonDataComunePolygons]);

  const setBivariatePropertyForComuneSimple = (feature, map) => {
    const geoprops = feature.properties;
    const comuneCode = geoprops["PRO_COM_T"];
    const bivariateValue = map[comuneCode];
    const updatedProperties = {
      ...geoprops,
      bivariateProperty: bivariateValue,
    };
    return updatedProperties;
  };

  const addBivariatePropertyForComuneSimple = (old, map) => ({
    ...old,
    features: old.features.map((feature) => ({
      ...feature,
      properties: setBivariatePropertyForComuneSimple(feature, map),
    })),
  });

  const addBivariatePropertyWrapper = async () => {
    const subIntervalsObject = getSubintervals(KPISelect, hazardSelect, props.variantProp);
    const cysHazardSelect = mapHazardCodeToHazardProperty[hazardSelect];
    const load = {
      refDate: props.wantedReferenceDate,
      aggr: "Map",
      filters: filterModelToQueryValue(props.filterModel),
      hazardSelect: getSimplifiedFromAnia(hazardSelect),
      cysHazardName: cysHazardSelect,
      KPISelect,
      subIntervals: subIntervalsObject,
    };

    const route =
      props.variantProp === "AgroClimateAnalytics"
        ? `proxy/agroanalytics_reale`
        : "analyticFilter/plainFilterPost";

    try {
      const { data: policiesResponse, error: message } = await httpClient().post(route, load);

      if (!policiesResponse?.mapCommuneIstatToBivariateLevel_1_9) {
        console.error(message);
        console.error("load = ", load, " hazardSelect = ", hazardSelect, " KPISelect = ", KPISelect);
        console.error(
          "policiesResponse?.mapCommuneIstatToBivariateLevel_1_9=",
          policiesResponse?.mapCommuneIstatToBivariateLevel_1_9,
        );
      }
      setGeojsonDataComunePolygonsFiltered((old) =>
        addBivariatePropertyForComuneSimple(old, policiesResponse?.mapCommuneIstatToBivariateLevel_1_9),
      );
    } catch (error) {
      apiError(error);
    }
  };

  const getJsonWithKPIforPeril = async (fileName) => {
    setIsLoading((old) => old + 1);
    await addBivariatePropertyWrapper();
    setIsLoading((old) => old - 1);
    setHazardSelectDone(hazardSelect);
    setKPISelectDone(KPISelect);
    setBivariateAdded(true);
  };

  // const [jsonWithKPIforPeril, setJsonWithKPIforPeril] = useState(null);

  const adjustKPIValuesForPeril = (hazardSelect) => {
    getJsonWithKPIforPeril(hazardSelect);
  };

  useEffect(() => {
    //console.log("useEffect,adjustKPIValuesForPeril");
    if (props.variantProp === "ClimateAnalytics" || props.variantProp === "AgroClimateAnalytics") {
      if (/*geojsonDataComunePolygons &&*/ geojsonDataComunePolygonsFiltered) {
        adjustKPIValuesForPeril(hazardSelect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hazardSelect, KPISelect]);

  const onMove = (viewState) => {
    setViewState(viewState);
    setCurrentZoom(viewState.zoom);
  };

  const fetchGenericGeoJson = async (setNormal, setFiltered, suffix, filter) => {
    setIsLoading((old) => old + 1);

    await fetch(site + filePrefix[props.page][0] + "/" + filePrefix[props.page][1] + suffix)
      .then((response) => response.json())
      .then((data) => {
        let geojsonData = data;
        if (filter) {
          // optional parameter
          const features = data.features.filter(filter);
          // The purpose is to remove the features with no_contratti==0, no_denunce==0 from points, but not from polygons
          geojsonData = { ...data, features };
        }

        if (isMountedRef.current) {
          // Only update if we're still mounted
          setNormal(geojsonData);
          setFiltered(geojsonData);
        }
      })
      .catch((error) => console.log(error));
    // TODO test
    setIsLoading((old) => old - 1);
  };

  const fetchComuneGeoJson = (propertyName) => {
    // Remove the features with no_contratti==0, no_denunce==0
    fetchGenericGeoJson(
      setGeojsonDataComunePoints,
      setGeojsonDataComunePointsFiltered,
      suffixComunePoints,
      (feature) => feature.properties[propertyName] !== 0,
    );
    fetchGenericGeoJson(setGeojsonDataComunePolygons, setGeojsonDataComunePolygonsFiltered, suffixComune);
  };

  const fetchProvGeoJson = (propertyName) => {
    // Remove the features with no_contratti==0, no_denunce==0
    fetchGenericGeoJson(
      setGeojsonDataProvPoints,
      setGeojsonDataProvPointsFiltered,
      suffixProvPoints,
      (feature) => feature.properties[propertyName] !== 0,
    );
    fetchGenericGeoJson(setGeojsonDataProvPolygons, setGeojsonDataProvPolygonsFiltered, suffixProv);
  };

  const fetchRegioneGeoJson = (propertyName) => {
    // Remove the features with no_contratti==0, no_denunce==0
    fetchGenericGeoJson(
      setGeojsonDataRegionePoints,
      setGeojsonDataRegionePointsFiltered,
      suffixRegionePoints,
      (feature) => feature.properties[propertyName] !== 0,
    );
    fetchGenericGeoJson(setGeojsonDataRegionePolygons, setGeojsonDataRegionePolygonsFiltered, suffixRegione);
  };

  const renderAdministrativeDataSources = () => (
    <>
      {/* Order of layers is important, the last one is on top of the others*/}
      {allJsons.comune &&
        geojsonDataComunePolygonsFiltered &&
        fillLayer("layer3", "src3", allJsons.comune, geojsonDataComunePolygonsFiltered)}
      {allJsons.prov &&
        geojsonDataProvPolygonsFiltered &&
        fillLayer("layer2", "src2", allJsons.prov, geojsonDataProvPolygonsFiltered)}
      {allJsons.regione &&
        geojsonDataRegionePolygonsFiltered &&
        fillLayer("layer1", "src1", allJsons.regione, geojsonDataRegionePolygonsFiltered)}
      {allJsons.comune &&
        geojsonDataComunePointsFiltered &&
        printNoContrattiAtPoint("txt-layer3", "txt-src3", allJsons.comune, geojsonDataComunePointsFiltered)}
      {allJsons.prov &&
        geojsonDataProvPointsFiltered &&
        printNoContrattiAtPoint("txt-layer2", "txt-src2", allJsons.prov, geojsonDataProvPointsFiltered)}
      {allJsons.regione &&
        geojsonDataRegionePointsFiltered &&
        printNoContrattiAtPoint("txt-layer1", "txt-src1", allJsons.regione, geojsonDataRegionePointsFiltered)}

      <LegendHelper
        json={allJsons.comune}
        geojson={geojsonDataComunePointsFiltered}
        currentZoom={currentZoom}
        variantProp={props.variantProp}
        filteredMapDataProp={props.filteredMapDataProp}
      ></LegendHelper>

      <LegendHelper
        json={allJsons.prov}
        geojson={geojsonDataProvPointsFiltered}
        currentZoom={currentZoom}
        variantProp={props.variantProp}
        filteredMapDataProp={props.filteredMapDataProp}
      ></LegendHelper>

      <LegendHelper
        json={allJsons.regione}
        geojson={geojsonDataRegionePointsFiltered}
        currentZoom={currentZoom}
        variantProp={props.variantProp}
        filteredMapDataProp={props.filteredMapDataProp}
      ></LegendHelper>
    </>
  );

  const renderBivariate = () => (
    <>
      {allJsons.bivariate &&
        geojsonDataComunePolygons &&
        fillLayer("layer5", "src5", allJsons.bivariate, geojsonDataComunePolygonsFiltered)}
      {false &&
        allJsons.bivariate &&
        geojsonDataComunePoints &&
        printNoContrattiAtPoint(
          "txt-layer5",
          "txt-src5",
          allJsons.bivariate,
          geojsonDataComunePointsFiltered,
        )}

      <LegendHelper
        json={allJsons.bivariate}
        geojson={geojsonDataComunePolygonsFiltered}
        currentZoom={currentZoom}
        extraObject={{ hazardSelectDone, KPISelectDone }}
        variantProp={props.variantProp}
        filteredMapDataProp={props.filteredMapDataProp}
      ></LegendHelper>
    </>
  );

  const isBivariate = () => {
    const ret = bivariateAdded && (hazardSelect !== "anyhazard" || KPISelect !== "none");
    // console.log("isBivariate() = ", ret, "bivariateAdded", bivariateAdded);
    return ret;
  };

  const showCommuneMarker = (param) => {
    if (param && geojsonDataComunePoints) {
      const foundComuneProp = geojsonDataComunePoints.features.filter((feature) => {
        return feature.properties.PRO_COM_T === param;
      });

      if (foundComuneProp.length === 1) {
        const coords = foundComuneProp[0].geometry.coordinates;

        return (
          <Marker longitude={coords[0]} latitude={coords[1]}>
            <Pin size={20} />
          </Marker>
        );
      }
    }
  };

  return (
    <>
      <div style={{ position: "relative" }}>
        <MapGL
          {...viewState}
          mapboxAccessToken={TOKEN}
          style={{ width: "100%", height: "600px" }}
          onMove={(evt) => onMove(evt.viewState)}
          mapStyle={mapStyle}
          ref={mapRef}
        >
          {!isBivariate() && renderAdministrativeDataSources()}

          {isBivariate() && renderBivariate()}

          {createMarkers(props, setSelectedPolicy)}

          {selectedPolicy &&
            smallPopupBoxCreation(
              selectedPolicy,
              setSelectedPolicy,
              getCompanyCodeName(selectedPolicy.companyCode, config.companyCodesMap),
            )}

          {defaultMapBoxStyle() &&
            MapStyleSelectorTool(mapSelect, setMapSelect, setMapStyle, defaultMapBoxStyle())}

          {(props.variantProp === "ClimateAnalytics" || props.variantProp === "AgroClimateAnalytics") &&
            HazardSelectorTool(hazardSelect, setHazardSelect, props.variantProp)}

          {(props.variantProp === "ClimateAnalytics" || props.variantProp === "AgroClimateAnalytics") &&
            KpiSelectorTool(KPISelect, setKPISelect, props.variantProp)}

          {(props.variantProp === "ClimateAnalytics" || props.variantProp === "AgroClimateAnalytics") &&
            showCommuneMarker(props.selectedComuneIstatProp)}

          {false && renderDownloadButton(allJsons, filePrefix[props.page][1])}

          {/*isLoading && <CircularProgress />*/}
        </MapGL>

        {isLoading !== 0 && (
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}>
            <CircularProgress size={100} style={{ opacity: 0.3 }} />
          </div>
        )}
        <ScaleBar zoom={viewState.zoom} />
      </div>
    </>
  );
};
